import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  TemplateRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MatTableModule,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import {
  MatPaginatorModule,
  MatPaginator,
  PageEvent,
} from '@angular/material/paginator';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

export interface TableColumn {
  name: string;
  header: string;
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'boolean'
    | 'icon'
    | 'button'
    | 'menu'
    | 'image'
    | 'toggle';
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  icon?: string;
  imageUrl?: string;
  tooltip?: string;
  action?: (row: any) => void;
  menuItems?: {
    label: string;
    icon?: string;
    action: (row: any) => void;
    disabled?: (row: any) => boolean;
  }[];
  cellTemplate?: TemplateRef<any>;
  valueFormatter?: (row: any) => string;
  customClass?: string;
  toggleAction?: (row: any, value: boolean) => void;
}

@Component({
  selector: 'app-mat-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
  ],
  templateUrl: './mat-table.component.html',
  styleUrl: './mat-table.component.scss',
})
export class MatTableComponent implements AfterViewInit, OnChanges {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<any>;

  @Input() columns: TableColumn[] = [];
  @Input() dataSource: any[] = [];
  @Input() displayedColumns: string[] = [];
  @Input() selectable = false;
  @Input() pageSize = 10;
  @Input() pageSizeOptions = [5, 10, 25, 100];
  @Input() showFirstLastButtons = true;
  @Input() loading = false;
  @Input() noDataMessage = 'No data available';
  @Input() stickyHeader = false;
  @Input() stickyFooter = false;
  @Input() height = 'auto';
  @Input() width = '100%';
  @Input() totalCount = 0;

  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<any>();

  selectedRows = new Set<any>();

  ngAfterViewInit() {
    // No need to set dataSource or paginator for server-side pagination
    // Sorting can be handled via sortChange event if needed
  }

  onSelectionChange(row: any) {
    if (this.selectedRows.has(row)) {
      this.selectedRows.delete(row);
    } else {
      this.selectedRows.add(row);
    }
    this.selectionChange.emit(Array.from(this.selectedRows));
  }

  isAllSelected() {
    return this.dataSource.length === this.selectedRows.size;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selectedRows.clear();
    } else {
      this.dataSource.forEach((row) => this.selectedRows.add(row));
    }
    this.selectionChange.emit(Array.from(this.selectedRows));
  }

  onRowClick(row: any) {
    this.rowClick.emit(row);
  }

  onSortChange(event: any) {
    this.sortChange.emit(event);
  }

  onPageChange(event: any) {
    this.pageChange.emit(event);
  }

  getColumnType(column: TableColumn): string {
    return column.type || 'text';
  }

  getColumnAlign(column: TableColumn): string {
    return column.align || 'left';
  }

  getColumnWidth(column: TableColumn): string {
    return column.width || 'auto';
  }

  getCellValue(row: any, column: TableColumn): any {
    if (!row || !column.name) return '';

    // Use valueFormatter if provided
    if (column.valueFormatter) {
      return column.valueFormatter(row);
    }

    // Handle nested properties
    const props = column.name.split('.');
    let value = row;

    for (const prop of props) {
      value = value?.[prop];
      if (value === undefined) return '';
    }

    return value;
  }

  isRowSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  getSelectedCount(): number {
    return this.selectedRows.size;
  }

  clearSelection() {
    this.selectedRows.clear();
    this.selectionChange.emit([]);
  }

  ngOnChanges(changes: SimpleChanges) {
    // No need to update a MatTableDataSource, just use the array
  }
}
